/**
 * AccessDrawer: right-side drawer showing exactly what the accountant
 * can and cannot do.
 *
 * State-aware: the status chip + timing chips render differently for
 * pending ("Invite pending" + Invited + Expires in N days) vs active
 * ("Connected" + Connected since <date>). The role banner and the
 * can/can't lists are the same across states.
 *
 * Opened from PendingCard's "Learn what X can see" link OR from
 * AccountantCard's "View access" button.
 */

import { useCallback, useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { X, Check, Shield, CircleOff } from "lucide-react";
import type { MyAccountant } from "../myAccountantTypes";
import {
  computeExpiryDate,
  formatDateShort,
  getInitials,
} from "../myAccountantUtils";
import { PALETTE } from "../constants";

interface AccessDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  accountant: MyAccountant;
}

interface AccessItem {
  label: string;
  desc: string;
}

const CAN_ITEMS: AccessItem[] = [
  {
    label: "Edit transactions, categories, reconciliations",
    desc: "Full read and write access to bank transactions, chart of accounts, and reconcile sessions.",
  },
  {
    label: "Run and export all accounting reports",
    desc: "Profit and loss, balance sheet, GST/HST, trial balance, general ledger, and everything under Reports.",
  },
  {
    label: "Prepare year-end books for your tax filing",
    desc: "Post adjusting entries, close the fiscal year, and export working papers for CRA filings.",
  },
  {
    label: "Create and manage recurring transactions",
    desc: "Set up and edit recurring bills, invoices, journals, and schedules.",
  },
];

const CANT_ITEMS: AccessItem[] = [
  {
    label: "Run payroll or change payroll settings",
    desc: "Payroll stays with you. You can still ask them to review pay run exports after you post.",
  },
  {
    label: "Invite other users or change permissions",
    desc: "Only owners can add users or change what anyone on the team can access.",
  },
  {
    label: "Delete the company or change your Novala subscription",
    desc: "Billing, plan changes, and company deletion stay with the owner.",
  },
];

export function AccessDrawer({ isOpen, onClose, accountant }: AccessDrawerProps) {
  const uid = useId().replace(/:/g, "");
  const styleTag = useAccessDrawerStyles(uid, accountant.status);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handleBackdrop = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const displayName = accountant.name?.trim() || accountant.email;
  const initials = getInitials(accountant.name || accountant.email, accountant.email);
  const emailFirmLine = accountant.firmName
    ? `${accountant.email} \u00b7 ${accountant.firmName}`
    : accountant.email;

  const firstName = accountant.name?.split(" ")[0] || displayName;
  const isActive = accountant.status === "active";

  const invitedShort = formatDateShort(accountant.invitedAt);
  const expiresAt = new Date(computeExpiryDate(accountant.invitedAt));
  const daysLeft = Math.max(
    0,
    Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
  );
  const connectedSince = accountant.acceptedAt
    ? formatDateShort(accountant.acceptedAt)
    : invitedShort;

  return createPortal(
    <>
      {styleTag}
      <div
        className={`acd-${uid}-backdrop`}
        onClick={handleBackdrop}
        aria-hidden="true"
      />
      <aside
        className={`acd-${uid}-drawer`}
        role="dialog"
        aria-labelledby={`acd-${uid}-title`}
        aria-modal="true"
      >
        <header className={`acd-${uid}-head`}>
          <button
            type="button"
            className={`acd-${uid}-close`}
            onClick={onClose}
            aria-label="Close access details"
          >
            <X size={18} strokeWidth={2} />
          </button>

          <div className={`acd-${uid}-identity`}>
            <div className={`acd-${uid}-avatar`} aria-hidden="true">
              {initials}
            </div>
            <div className={`acd-${uid}-identity-text`}>
              <h2 className={`acd-${uid}-name`} id={`acd-${uid}-title`}>
                {displayName}
              </h2>
              <p className={`acd-${uid}-email`}>{emailFirmLine}</p>
            </div>
          </div>

          <div className={`acd-${uid}-chips`}>
            <span className={`acd-${uid}-chip`}>
              <span className={`acd-${uid}-chip-dot`} aria-hidden="true" />
              <strong>{isActive ? "Connected" : "Invite pending"}</strong>
            </span>
            <span className={`acd-${uid}-chip-sep`} />
            {isActive ? (
              <span className={`acd-${uid}-chip`}>
                Connected since <strong>{connectedSince}</strong>
              </span>
            ) : (
              <>
                <span className={`acd-${uid}-chip`}>
                  Invited <strong>{invitedShort}</strong>
                </span>
                <span className={`acd-${uid}-chip-sep`} />
                <span className={`acd-${uid}-chip`}>
                  Expires in <strong>{daysLeft} days</strong>
                </span>
              </>
            )}
          </div>
        </header>

        <div className={`acd-${uid}-role`}>
          <div className={`acd-${uid}-role-icon`} aria-hidden="true">
            <Shield size={18} strokeWidth={2} />
          </div>
          <div className={`acd-${uid}-role-text`}>
            <p className={`acd-${uid}-role-title`}>Accountant access</p>
            <p className={`acd-${uid}-role-sub`}>
              The standard Novala access level for a CPA. Full read and write
              on books and reports; no payroll, user, or billing control.
            </p>
          </div>
        </div>

        <div className={`acd-${uid}-body`}>
          <section className={`acd-${uid}-section`}>
            <h3 className={`acd-${uid}-section-head`}>What {firstName} can do</h3>
            <ul className={`acd-${uid}-list`}>
              {CAN_ITEMS.map((item) => (
                <li key={item.label} className={`acd-${uid}-item`}>
                  <span className={`acd-${uid}-item-icon acd-${uid}-can`}>
                    <Check size={14} strokeWidth={2.6} />
                  </span>
                  <div className={`acd-${uid}-item-body`}>
                    <p className={`acd-${uid}-item-label`}>{item.label}</p>
                    <p className={`acd-${uid}-item-desc`}>{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className={`acd-${uid}-section`}>
            <h3 className={`acd-${uid}-section-head`}>
              What {firstName} can't do
            </h3>
            <ul className={`acd-${uid}-list`}>
              {CANT_ITEMS.map((item) => (
                <li key={item.label} className={`acd-${uid}-item`}>
                  <span className={`acd-${uid}-item-icon acd-${uid}-cant`}>
                    <CircleOff size={14} strokeWidth={2} />
                  </span>
                  <div className={`acd-${uid}-item-body`}>
                    <p className={`acd-${uid}-item-label`}>{item.label}</p>
                    <p className={`acd-${uid}-item-desc`}>{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <footer className={`acd-${uid}-foot`}>
          <p className={`acd-${uid}-foot-left`}>
            You can remove {firstName} anytime from the My accountant page.
          </p>
          <button
            type="button"
            className={`acd-${uid}-btn-primary`}
            onClick={onClose}
          >
            Got it
          </button>
        </footer>
      </aside>
    </>,
    document.body,
  );
}

function useAccessDrawerStyles(uid: string, status: MyAccountant["status"]) {
  const dotColor = status === "active" ? PALETTE.teal : PALETTE.amber;
  const dotAnim = status === "active" ? "none" : `acd-${uid}-pulse 1.6s ease-in-out infinite`;
  const strongColor = status === "active" ? PALETTE.teal : PALETTE.amber;

  const css = `
    .acd-${uid}-backdrop {
      position: fixed; inset: 0;
      background: rgba(14, 26, 26, 0.42);
      z-index: 1000;
      animation: acd-${uid}-fade 180ms ease-out;
    }
    .acd-${uid}-drawer {
      position: fixed;
      top: 0; right: 0; bottom: 0;
      width: 540px;
      max-width: 100vw;
      background: #ffffff;
      z-index: 1001;
      display: flex;
      flex-direction: column;
      box-shadow: -8px 0 24px -8px rgba(14, 26, 26, 0.18);
      animation: acd-${uid}-slide 220ms cubic-bezier(0.22, 1, 0.36, 1);
    }
    @keyframes acd-${uid}-fade {
      from { opacity: 0; } to { opacity: 1; }
    }
    @keyframes acd-${uid}-slide {
      from { transform: translateX(100%); } to { transform: translateX(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      .acd-${uid}-backdrop, .acd-${uid}-drawer { animation: none; }
    }

    .acd-${uid}-head {
      padding: 22px 26px 20px;
      border-bottom: 1px solid ${PALETTE.lineSoft};
      position: relative;
    }
    .acd-${uid}-close {
      position: absolute;
      top: 16px; right: 18px;
      background: transparent;
      border: 0;
      padding: 6px;
      border-radius: 7px;
      color: ${PALETTE.ink2};
      cursor: pointer;
      font-family: inherit;
    }
    .acd-${uid}-close:hover { background: ${PALETTE.lineSoft}; }

    .acd-${uid}-identity {
      display: flex;
      align-items: center;
      gap: 14px;
      margin-bottom: 16px;
    }
    .acd-${uid}-avatar {
      width: 48px; height: 48px;
      border-radius: 50%;
      background: ${PALETTE.ink};
      color: #fff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 15px;
      letter-spacing: 0.02em;
      flex-shrink: 0;
    }
    .acd-${uid}-identity-text { min-width: 0; }
    .acd-${uid}-name {
      font-size: 17px;
      font-weight: 700;
      color: ${PALETTE.ink};
      margin: 0 0 2px;
      letter-spacing: -0.012em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 400px;
    }
    .acd-${uid}-email {
      font-size: 12.5px;
      color: ${PALETTE.ink3};
      margin: 0;
      font-weight: 500;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .acd-${uid}-chips {
      display: flex;
      align-items: center;
      gap: 14px;
      flex-wrap: wrap;
      font-size: 12px;
      color: ${PALETTE.ink2};
    }
    .acd-${uid}-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-weight: 500;
    }
    .acd-${uid}-chip strong { color: ${PALETTE.ink}; font-weight: 600; margin-left: 4px; }
    .acd-${uid}-chip:first-child strong { color: ${strongColor}; margin-left: 0; }
    .acd-${uid}-chip-dot {
      width: 7px; height: 7px;
      border-radius: 50%;
      background: ${dotColor};
      display: inline-block;
      animation: ${dotAnim};
      margin-right: 2px;
    }
    @keyframes acd-${uid}-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.4; }
    }
    @media (prefers-reduced-motion: reduce) {
      .acd-${uid}-chip-dot { animation: none; }
    }
    .acd-${uid}-chip-sep { width: 1px; height: 11px; background: ${PALETTE.line}; }

    .acd-${uid}-role {
      background: #F3F5F7;
      border: 1px solid #E1E6EC;
      border-radius: 10px;
      padding: 14px 16px;
      margin: 20px 26px 0;
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .acd-${uid}-role-icon {
      width: 36px; height: 36px;
      border-radius: 8px;
      background: ${PALETTE.teal};
      color: #fff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .acd-${uid}-role-text { flex: 1; min-width: 0; }
    .acd-${uid}-role-title {
      font-size: 13px;
      font-weight: 700;
      color: ${PALETTE.ink};
      margin: 0 0 2px;
      letter-spacing: 0.01em;
    }
    .acd-${uid}-role-sub {
      font-size: 12px;
      color: ${PALETTE.ink2};
      margin: 0;
      line-height: 1.5;
    }

    .acd-${uid}-body {
      flex: 1;
      overflow-y: auto;
      padding: 24px 26px 10px;
    }
    .acd-${uid}-section { margin-bottom: 26px; }
    .acd-${uid}-section:last-child { margin-bottom: 0; }
    .acd-${uid}-section-head {
      font-size: 10.5px;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      font-weight: 700;
      color: ${PALETTE.ink3};
      margin: 0 0 12px;
    }
    .acd-${uid}-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .acd-${uid}-item {
      display: grid;
      grid-template-columns: 22px 1fr;
      gap: 12px;
      padding: 10px 0;
      border-bottom: 1px solid ${PALETTE.lineSoft};
      align-items: flex-start;
    }
    .acd-${uid}-item:last-child { border-bottom: 0; }
    .acd-${uid}-item-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-top: 2px;
    }
    .acd-${uid}-can { color: ${PALETTE.teal}; }
    .acd-${uid}-cant { color: ${PALETTE.ink4}; }
    .acd-${uid}-item-body { min-width: 0; }
    .acd-${uid}-item-label {
      font-size: 13px;
      font-weight: 600;
      color: ${PALETTE.ink};
      margin: 0 0 2px;
      letter-spacing: -0.003em;
    }
    .acd-${uid}-item-desc {
      font-size: 12px;
      color: ${PALETTE.ink3};
      margin: 0;
      line-height: 1.5;
    }

    .acd-${uid}-foot {
      padding: 14px 26px 20px;
      border-top: 1px solid ${PALETTE.lineSoft};
      background: #FBFBFC;
      display: flex;
      justify-content: space-between;
      gap: 10px;
      align-items: center;
    }
    .acd-${uid}-foot-left {
      font-size: 12px;
      color: ${PALETTE.ink3};
      max-width: 300px;
      line-height: 1.5;
      margin: 0;
    }
    .acd-${uid}-btn-primary {
      background: ${PALETTE.ink};
      color: #ffffff;
      border: 0;
      padding: 9px 18px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: opacity 160ms, transform 160ms;
    }
    .acd-${uid}-btn-primary:hover {
      opacity: 0.92;
      transform: translateY(-1px);
    }

    @media (max-width: 560px) {
      .acd-${uid}-chips { gap: 10px; }
      .acd-${uid}-chip-sep { display: none; }
      .acd-${uid}-foot { flex-direction: column; align-items: stretch; }
      .acd-${uid}-foot-left { max-width: 100%; }
    }
  `;
  return <style>{css}</style>;
}